import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../../lib/axiosInstance';
import Layout from 'src/layout';
import { useNavigate } from 'react-router-dom';
import { getPaginatedProducts, getProductCount } from '../../lib/api/productsApi';
import { toast } from 'react-toastify';
import TableSkeleton from '../../components/ui/tableSkeleton';
import {
    RiAddLine as AddIcon,
    RiEditLine as EditIcon,
    RiDeleteBinLine as DeleteIcon,
    RiSearchLine as SearchIcon,
    RiRefreshLine as RefreshIcon,
    RiPriceTag3Line as TagIcon,
    RiShoppingBag3Line as BagIcon,
    RiArrowLeftSLine as PrevIcon,
    RiArrowRightSLine as NextIcon
} from 'react-icons/ri';

const ListCombo = () => {
    const navigate = useNavigate();

    const [products, setProducts] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);

    const [filters, setFilters] = useState({
        name: '',
        productID: '',
        type: 'combo'
    });

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const limit = 10;

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const fetchProductCount = useCallback(async () => {
        try {
            const res = await getProductCount(filters);
            const count = res?.totalItems || 0;
            setTotalItems(count);
            setTotalPages(Math.ceil(count / limit) || 1);
        } catch (error) {
            console.error('Error counting combo products:', error);
        }
    }, [filters, limit]);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getPaginatedProducts({
                page,
                limit,
                filters
            });
            setProducts(response?.data || []);
        } catch (error) {
            console.error('Error fetching combo products:', error);
            toast.error('Failed to load combo products');
        } finally {
            setLoading(false);
        }
    }, [page, limit, filters]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchProductCount();
        fetchProducts();
    };

    const handleReset = () => {
        setFilters({ name: '', productID: '', type: 'combo' });
        setPage(1);
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!productToDelete) return;
        try {
            setDeleting(true);
            await axiosInstance.delete(`/combo/delete/${productToDelete.productID}`);
            toast.success('Combo package deleted successfully');
            setDeleteModalOpen(false);
            setProductToDelete(null);
            fetchProductCount();
            fetchProducts();
        } catch (error) {
            console.error('Failed to delete combo:', error);
            toast.error(error.response?.data?.message || error.message || 'Failed to delete combo');
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        fetchProductCount();
        fetchProducts();
    }, [page]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    return (
        <Layout title="Combo Bundles" active="admin-combo-list">
            <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">

                {/* 🚀 HEADER BANNER */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                            <TagIcon className="text-indigo-600" /> Combo Bundles
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Manage pre-configured product packages, combo deals, and fixed multi-item discounts.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => { fetchProductCount(); fetchProducts(); }}
                            className="p-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 transition"
                            title="Refresh Data"
                        >
                            <RefreshIcon className={loading ? 'animate-spin text-indigo-600' : ''} />
                        </button>

                        <button
                            onClick={() => navigate('/combo/add')}
                            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition"
                        >
                            <AddIcon className="text-lg" /> Add Combo Bundle
                        </button>
                    </div>
                </div>

                {/* 🔍 FILTERS BAR */}
                <form onSubmit={handleSearch} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-3 items-center">
                    <div className="relative flex-1 w-full">
                        <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                            placeholder="Search by Combo Title..."
                            className="w-full pl-10 pr-4 py-2 text-xs font-medium border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 bg-gray-50/50"
                        />
                    </div>

                    <div className="relative w-full md:w-48">
                        <input
                            type="text"
                            name="productID"
                            value={filters.productID}
                            onChange={handleFilterChange}
                            placeholder="Filter by Product ID..."
                            className="w-full px-4 py-2 text-xs font-medium border border-gray-200 rounded-xl focus:outline-none focus:border-indigo-500 bg-gray-50/50"
                        />
                    </div>

                    <div className="flex items-center gap-2 w-full md:w-auto">
                        <button
                            type="submit"
                            className="w-full md:w-auto px-5 py-2 bg-gray-900 hover:bg-gray-800 text-white text-xs font-semibold rounded-xl transition"
                        >
                            Filter
                        </button>
                        <button
                            type="button"
                            onClick={handleReset}
                            className="w-full md:w-auto px-4 py-2 border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold rounded-xl transition"
                        >
                            Reset
                        </button>
                    </div>
                </form>

                {/* 📋 TABLE CONTAINER */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                    {loading ? (
                        <TableSkeleton rows={5} />
                    ) : products.length === 0 ? (
                        <div className="text-center py-16 px-4">
                            <div className="w-16 h-16 rounded-full bg-indigo-50 text-indigo-500 flex items-center justify-center mx-auto mb-3">
                                <BagIcon className="text-3xl" />
                            </div>
                            <h3 className="text-base font-bold text-gray-900">No Combo Bundles Found</h3>
                            <p className="text-xs text-gray-500 max-w-sm mx-auto mt-1">
                                Create pre-configured product combo packages to boost average order value.
                            </p>
                            <button
                                onClick={() => navigate('/combo/add')}
                                className="mt-4 inline-flex items-center gap-2 bg-indigo-600 text-white text-xs font-semibold px-4 py-2 rounded-xl"
                            >
                                <AddIcon /> Create Combo Bundle
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                                        <th className="py-3.5 px-4">Combo Product</th>
                                        <th className="py-3.5 px-4">Product ID</th>
                                        <th className="py-3.5 px-4 text-right">Regular Price</th>
                                        <th className="py-3.5 px-4 text-right">Sale Price</th>
                                        <th className="py-3.5 px-4 text-center">Discount Type</th>
                                        <th className="py-3.5 px-4 text-center">Created Date</th>
                                        <th className="py-3.5 px-4 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-xs font-medium text-gray-700">
                                    {products.map((product) => {
                                        let imgUrl = '';
                                        try {
                                            const images = typeof product.featuredImage === 'string'
                                                ? JSON.parse(product.featuredImage)
                                                : product.featuredImage;
                                            imgUrl = Array.isArray(images) ? images[0]?.imgUrl : (images?.imgUrl || '');
                                        } catch (e) {
                                            imgUrl = '';
                                        }

                                        return (
                                            <tr key={product.productID} className="hover:bg-gray-50/80 transition">
                                                {/* Product Info */}
                                                <td className="py-3.5 px-4">
                                                    <div className="flex items-center gap-3">
                                                        {imgUrl ? (
                                                            <img
                                                                src={imgUrl}
                                                                alt={product.name}
                                                                className="h-10 w-10 rounded-xl object-cover border border-gray-100 shadow-sm"
                                                            />
                                                        ) : (
                                                            <div className="h-10 w-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-xs">
                                                                N/A
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="font-bold text-gray-900 line-clamp-1 hover:text-indigo-600 cursor-pointer" onClick={() => navigate(`/combo/detail/${product.productID}`)}>
                                                                {product.name}
                                                            </div>
                                                            <span className="text-[10px] text-purple-600 font-semibold uppercase bg-purple-50 px-1.5 py-0.5 rounded border border-purple-100">
                                                                {product.type || 'Combo'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Product ID */}
                                                <td className="py-3.5 px-4 font-mono text-gray-500 text-[11px]">
                                                    {product.productID}
                                                </td>

                                                {/* Regular Price */}
                                                <td className="py-3.5 px-4 text-right font-bold text-gray-900">
                                                    {formatCurrency(product.regularPrice)}
                                                </td>

                                                {/* Sale Price */}
                                                <td className="py-3.5 px-4 text-right font-black text-emerald-600">
                                                    {product.salePrice ? formatCurrency(product.salePrice) : '-'}
                                                </td>

                                                {/* Discount Type */}
                                                <td className="py-3.5 px-4 text-center">
                                                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200 capitalize">
                                                        {product.discountType || 'Fixed'}
                                                    </span>
                                                </td>

                                                {/* Created Date */}
                                                <td className="py-3.5 px-4 text-center text-gray-500">
                                                    {new Date(product.createdAt).toLocaleDateString('en-IN', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric'
                                                    })}
                                                </td>

                                                {/* Actions */}
                                                <td className="py-3.5 px-4 text-center">
                                                    <div className="flex items-center justify-center gap-1.5">
                                                        <button
                                                            onClick={() => navigate(`/combo/detail/${product.productID}`)}
                                                            className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 transition border border-indigo-100"
                                                            title="Edit Combo Bundle"
                                                        >
                                                            <EditIcon className="text-base" />
                                                        </button>

                                                        <button
                                                            onClick={() => handleDeleteClick(product)}
                                                            className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 transition border border-rose-100"
                                                            title="Delete Combo"
                                                        >
                                                            <DeleteIcon className="text-base" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* 🔄 PAGINATION */}
                    {!loading && totalPages > 1 && (
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-50/50 border-t border-gray-100 text-xs">
                            <span className="text-gray-500 font-medium">
                                Showing Page <strong className="text-gray-900">{page}</strong> of <strong className="text-gray-900">{totalPages}</strong> ({totalItems} items)
                            </span>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-700 font-semibold transition"
                                >
                                    <PrevIcon /> Prev
                                </button>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 text-gray-700 font-semibold transition"
                                >
                                    Next <NextIcon />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* DELETE CONFIRMATION MODAL */}
            {deleteModalOpen && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-sm w-full p-5 border border-gray-100 shadow-xl space-y-4">
                        <h3 className="text-base font-bold text-gray-900">Delete Combo Bundle?</h3>
                        <p className="text-xs text-gray-500">
                            Are you sure you want to delete <strong className="text-gray-900">{productToDelete?.name}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex items-center justify-end gap-3 pt-2">
                            <button
                                onClick={() => setDeleteModalOpen(false)}
                                className="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-100 rounded-xl"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="px-4 py-2 text-xs font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-xl disabled:opacity-50"
                            >
                                {deleting ? 'Deleting...' : 'Confirm Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default ListCombo;